import { gql, useMutation } from '@apollo/client'
import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import { DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { DigitalAsset, Maybe } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import moment from 'moment'
import { memo } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import { currentRetailerAccountIdSelector } from '@src/stores/app/selectors'

import MobileFullScreenDialog from '../shared/MobileFullScreenDialog'
import useTracking from '../shared/Tracking/useTracking'

import DigitalAssetIcon from './DigitalAssetIcon'
import Media from './Media'

const useStyles = makeStyles()((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(3),
  },
  textTitle: {
    marginRight: theme.spacing(4),
  },
  outlined: {
    padding: 12,
  },
  detailsContainer: {
    padding: theme.spacing(4),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}))

type DigitalAssetDialogProps = Omit<DialogProps, 'onClose'> & {
  digitalAsset?: Maybe<DigitalAsset>
  onClose: () => void
}

const DigitalAssetDialog: React.FC<
  React.PropsWithChildren<DigitalAssetDialogProps>
> = ({ open, onClose, digitalAsset }) => {
  const { track } = useTracking()
  const { classes } = useStyles()
  const [recordDownload] = useMutation(RECORD_DOWNLOAD)
  const currentRetailerAccountId = useSelector(currentRetailerAccountIdSelector)
  const downloadUrl = digitalAsset?.download_url || ''

  const handleDownloadTracking = () => {
    track({
      event: 'Digital Asset Downloaded',
      data: {
        retailer_account_id: currentRetailerAccountId,
        digital_asset_id: digitalAsset?.id,
      },
    })
    recordDownload({
      variables: {
        params: {
          resource_id: currentRetailerAccountId,
          resource_type: 'retailer_accounts',
          digital_asset_id: digitalAsset?.id,
        },
      },
    })
  }

  return (
    <MobileFullScreenDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <DigitalAssetIcon
          fileName={digitalAsset?.name || ''}
          className={classes.icon}
        />
        {digitalAsset?.name}

        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent data-testid={'digitalAssetDetails'}>
        {digitalAsset && <Media digitalAsset={digitalAsset} width={700} />}
        {digitalAsset && <DigitalAssetDetails digitalAsset={digitalAsset} />}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          // HACK Because the spacing in DialogActions is done with a
          // `:first-of-type` selector, if the elements don't all have the same
          // type then the spacing gets thrown off.
          // Since we want to use `<Button href="..." download>` for the
          // download button, which uses an <a> element, we should match that
          // here.
          // This seems to be a limitation of CSS-in-JS / something that _can_
          // go wrong, but upstream can't know if you're doing things correctly.
          // https://github.com/mui/material-ui/issues/36947
          component="a"
          data-testid={'dialogCloseButton'}
        >
          {I18n.t('shared.close')}
        </Button>

        <Button
          onClick={handleDownloadTracking}
          href={downloadUrl}
          target={'_blank'}
          variant="contained"
          color="primary"
          download={digitalAsset?.name || ''}
          data-testid={'dialogDownloadButton'}
        >
          {I18n.t('downloadables.download')}
        </Button>
      </DialogActions>
    </MobileFullScreenDialog>
  )
}

export default memo(DigitalAssetDialog)

export const DigitalAssetDetails = ({
  digitalAsset,
}: {
  digitalAsset: DigitalAsset
}) => {
  const { classes } = useStyles()

  if (!digitalAsset) return null
  return (
    <div className={classes.detailsContainer}>
      <Grid container spacing={4}>
        {digitalAsset.file && (
          <Grid container item xs={6} sm={4} alignItems="center">
            <Typography className={classes.textTitle} variant="subtitle2">
              {I18n.t('downloadables.size')}:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {I18n.toHumanSize(digitalAsset.file?.bytes || 0)}
            </Typography>
          </Grid>
        )}
        {digitalAsset.file && digitalAsset.resource_type !== 'raw' && (
          <Grid container item xs={6} sm={4}>
            <Typography className={classes.textTitle} variant="subtitle2">
              {I18n.t('downloadables.dimensions')}:
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {`${digitalAsset.file?.width} x ${digitalAsset.file?.height}`}
            </Typography>
          </Grid>
        )}
        <Grid container item xs={6} sm={4}>
          <Typography className={classes.textTitle} variant="subtitle2">
            {I18n.t('downloadables.created')}:
          </Typography>
          <Typography color="textSecondary" variant="body2">
            {moment(digitalAsset.created_at || '').format('L')}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        {digitalAsset.description && (
          <Grid container item xs={6} sm={4}>
            <Typography className={classes.textTitle} variant="subtitle2">
              {I18n.t('downloadables.description')}:
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {digitalAsset.description}
            </Typography>
          </Grid>
        )}
        {digitalAsset.tag_list && (
          <Grid container item xs={6} sm={4}>
            <Typography variant="subtitle2" className={classes.textTitle}>
              {I18n.t('downloadables.tags')}:
            </Typography>
            <Box display="flex" alignItems="center">
              {digitalAsset.tag_list?.map((tag: string) => (
                <Chip size="small" label={tag} style={{ marginRight: '4px' }} />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </div>
  )
}

const RECORD_DOWNLOAD = gql`
  mutation recordDownloadDigitalAsset(
    $params: RecordDownloadDigitalAssetParams!
  ) {
    recordDownloadDigitalAsset(params: $params)
  }
`
